import type { CSSProperties } from 'react';
import type { FormaPagamento, SituacaoPagamentoMarcacao } from '@arena/shared/dominio';
import {
  ROTULO_TIPO_MARCACAO,
  type CorDeCard,
  type MarcacaoNaGrade,
  type QuadraDaGrade,
} from '@arena/shared/schemas';
import { partesNoFuso } from '@arena/shared/tempo';

/**
 * Os textos e cores da agenda. Funções puras, testadas em
 * `layout.test.ts`.
 *
 * Toda hora exibida passa por `partesNoFuso(instante, fuso)` com o fuso
 * DA ARENA. Um `toLocaleTimeString()` sem `timeZone` aqui mostraria a
 * aula das 19h no horário do computador da recepção — é o bug que o
 * critério de aceite do documento 06 proíbe.
 */

// ---------------------------------------------------------------------
// Tempo
// ---------------------------------------------------------------------

export function horaNaArena(instante: string, fuso: string): string {
  return partesNoFuso(instante, fuso).hora;
}

/** "19:00" → "19h"; "19:30" → "19h30". O formato curto do protótipo. */
export function horaCurta(hora: string): string {
  const [h, m] = hora.split(':');
  return m === '00' ? `${h}h` : `${h}h${m}`;
}

/** "07h às 09h" — como nas janelas livres do protótipo. */
export function intervaloCurto(inicio: string, fim: string, fuso: string): string {
  return `${horaCurta(horaNaArena(inicio, fuso))} às ${horaCurta(horaNaArena(fim, fuso))}`;
}

/** 840 → "14 h"; 90 → "1h30"; 30 → "30 min". */
export function horas(minutos: number): string {
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h} h` : `${h}h${String(m).padStart(2, '0')}`;
}

/** As opções do seletor de duração: "1 hora", "1h30", "2 horas". */
export function rotuloDeDuracao(minutos: number): string {
  if (minutos < 60) return `${minutos} min`;
  if (minutos % 60 !== 0) return horas(minutos);
  const h = minutos / 60;
  return h === 1 ? '1 hora' : `${h} horas`;
}

/**
 * "Terça, 2 de setembro" — o cabeçalho de `#v-agenda`.
 *
 * A data de calendário é lida em UTC ao meio-dia de propósito: é uma
 * data, não um instante, e ao meio-dia UTC nenhum fuso do planeta a
 * empurra para o dia vizinho.
 */
export function dataPorExtenso(data: string, comAno = false): string {
  const texto = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(comAno ? { year: 'numeric' } : {}),
  }).format(new Date(`${data}T12:00:00Z`));
  const semFeira = texto.replace('-feira', '');
  return semFeira.charAt(0).toUpperCase() + semFeira.slice(1);
}

/** "16/03". */
export function dataCurta(data: string): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}`;
}

export const NOMES_CURTOS_DOS_DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

const NOMES_DOS_DIAS_NO_PLURAL = [
  'domingos',
  'segundas',
  'terças',
  'quartas',
  'quintas',
  'sextas',
  'sábados',
];

/** [2, 4] → "terças e quintas". */
export function diasPorExtenso(dias: readonly number[]): string {
  const nomes = dias.map((d) => NOMES_DOS_DIAS_NO_PLURAL[d] ?? '?');
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

// ---------------------------------------------------------------------
// Dinheiro
// ---------------------------------------------------------------------

/** Centavos → "R$ 1.240" ou "R$ 120,50". Número inteiro não mostra ",00". */
export function dinheiro(centavos: number): string {
  const inteiro = centavos % 100 === 0;
  return (
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: inteiro ? 0 : 2,
      maximumFractionDigits: 2,
    })
      .format(centavos / 100)
      // O Intl separa "R$" do número com espaço inseparável.
      .replace(/\s/g, ' ')
  );
}

/**
 * O que a recepção digita no campo de valor → centavos.
 *
 * "120", "120,50", "R$ 1.240,00" e "1240.5" valem. Vírgula é decimal;
 * ponto é milhar — a não ser que seja o único separador e venha com um
 * ou dois dígitos depois, que é como alguém digita "120.5".
 */
export function paraCentavos(texto: string): number | null {
  const limpo = texto.replace(/[R$\s]/g, '');
  if (limpo === '') return null;

  let normalizado: string;
  if (limpo.includes(',')) {
    normalizado = limpo.replace(/\./g, '').replace(',', '.');
  } else if (/^\d+\.\d{1,2}$/.test(limpo)) {
    normalizado = limpo;
  } else {
    normalizado = limpo.replace(/\./g, '');
  }

  if (!/^\d+(\.\d{1,2})?$/.test(normalizado)) return null;
  return Math.round(Number(normalizado) * 100);
}

// ---------------------------------------------------------------------
// Pagamento
// ---------------------------------------------------------------------

/**
 * As cinco opções de `#m-reserva`, como pares (forma, situação). "Sinal"
 * é situação, não forma: é Pix com parte paga.
 */
export const PRESETS_DE_PAGAMENTO = [
  { chave: 'pix_pago', rotulo: 'Pix — pago', forma: 'Pix', situacao: 'Pago' },
  { chave: 'pix_sinal', rotulo: 'Pix — sinal', forma: 'Pix', situacao: 'Sinal' },
  { chave: 'dinheiro', rotulo: 'Dinheiro na chegada', forma: 'Dinheiro', situacao: 'APagar' },
  { chave: 'cartao', rotulo: 'Cartão', forma: 'Cartao', situacao: 'APagar' },
  {
    chave: 'mensalidade',
    rotulo: 'Lançar na mensalidade',
    forma: 'Mensalidade',
    situacao: 'NaMensalidade',
  },
] as const satisfies readonly {
  chave: string;
  rotulo: string;
  forma: FormaPagamento;
  situacao: SituacaoPagamentoMarcacao;
}[];

export type ChaveDePagamento = (typeof PRESETS_DE_PAGAMENTO)[number]['chave'];

export function presetDoPagamento(
  forma: string | null | undefined,
  situacao: string | null | undefined,
): ChaveDePagamento | '' {
  return (
    PRESETS_DE_PAGAMENTO.find((p) => p.forma === forma && p.situacao === situacao)?.chave ?? ''
  );
}

const NOME_DA_FORMA: Record<string, string> = {
  Pix: 'Pix',
  Dinheiro: 'Dinheiro',
  Cartao: 'Cartão',
  Boleto: 'Boleto',
  Mensalidade: 'Mensalidade',
};

/** O selo do card: "Pix pago", "a pagar", "sinal". `null` sem situação. */
export function seloDoPagamento(
  forma: string | null | undefined,
  situacao: string | null | undefined,
): string | null {
  switch (situacao) {
    case 'Pago':
      return forma ? `${NOME_DA_FORMA[forma] ?? forma} pago` : 'pago';
    case 'Sinal':
      return 'sinal pago';
    case 'APagar':
      return 'a pagar';
    case 'NaMensalidade':
      return 'na mensalidade';
    case 'Isento':
      return 'isento';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------
// Aparência do card
// ---------------------------------------------------------------------

/**
 * As cores de `#m-reserva` — "Padrão da quadra", "Ocre", "Azul",
 * "Grafite" —, apontando para os tokens. A paleta mora em tokens.css;
 * aqui só o nome.
 *
 * O valor que vai para a API continua sendo o de sempre (`laranja`,
 * `vinho`): é contrato com o servidor. O que mudou foi a cor que cada um
 * desenha e, por isso, o rótulo.
 */
export const CORES_DO_CARD: readonly {
  valor: CorDeCard | 'padrao';
  rotulo: string;
  token: string;
}[] = [
  { valor: 'padrao', rotulo: 'Padrão da quadra', token: 'var(--quadra-1)' },
  { valor: 'laranja', rotulo: 'Ocre', token: 'var(--serie-4)' },
  { valor: 'azul', rotulo: 'Azul', token: 'var(--q2)' },
  { valor: 'vinho', rotulo: 'Grafite', token: 'var(--serie-3)' },
];

const CLASSE_DA_COR: Record<CorDeCard, string> = {
  vermelho: 'q1',
  laranja: 'exp',
  azul: 'q2',
  vinho: 'wine',
};

/**
 * A classe e o estilo de um card da grade.
 *
 * Bloqueio tem identidade própria (listrado, texto apagado). Cor escolhida vence a da quadra. Sem escolha, a cor é a da
 * QUADRA — que é configuração da arena, não constante: por isso vem
 * como estilo, e o fundo translúcido é derivado dela com `color-mix`,
 * como o protótipo faz com o vermelho e o azul.
 */
export function aparenciaDoCard(
  marcacao: Pick<MarcacaoNaGrade, 'tipo' | 'cor'>,
  quadra: Pick<QuadraDaGrade, 'corPadrao'> | undefined,
): { classe: string; estilo?: CSSProperties } {
  if (marcacao.tipo === 'Bloqueio') return { classe: 'ev block' };
  if (marcacao.cor) return { classe: `ev ${CLASSE_DA_COR[marcacao.cor]}` };
  const cor = quadra?.corPadrao ?? 'var(--line)';
  return {
    classe: 'ev',
    estilo: {
      borderColor: cor,
      background: `color-mix(in srgb, ${cor} 16%, transparent)`,
    },
  };
}

const TOKEN_DA_COR: Record<CorDeCard, string> = {
  vermelho: 'var(--quadra-1)',
  laranja: 'var(--serie-4)',
  azul: 'var(--q2)',
  vinho: 'var(--serie-3)',
};

/** A cor cheia — o quadradinho do feed e do cabeçalho de `#m-evento`. */
export function corSolidaDaMarcacao(
  marcacao: Pick<MarcacaoNaGrade, 'tipo' | 'cor'>,
  quadra: Pick<QuadraDaGrade, 'corPadrao'> | undefined,
): string {
  if (marcacao.tipo === 'Bloqueio') return 'var(--serie-5)';
  if (marcacao.cor) return TOKEN_DA_COR[marcacao.cor];
  return quadra?.corPadrao || 'var(--panel-3)';
}

/** "62999991180" → "(62) 9 9999-1180". O que vier fora do padrão passa como está. */
export function telefoneFormatado(digitos: string): string {
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos[2]} ${digitos.slice(3, 7)}-${digitos.slice(7)}`;
  }
  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }
  return digitos;
}

/** O título do card: o nome dado, ou o tipo. */
export function tituloDaMarcacao(marcacao: Pick<MarcacaoNaGrade, 'tipo' | 'titulo'>): string {
  return marcacao.titulo ?? ROTULO_TIPO_MARCACAO[marcacao.tipo];
}

/** A linha de baixo do card: "Marcos Vinícius · 8 pessoas". */
export function subtituloDaMarcacao(
  marcacao: Pick<MarcacaoNaGrade, 'tipo' | 'responsavelNome' | 'pessoas' | 'titulo'>,
): string {
  if (marcacao.tipo === 'Bloqueio') return 'bloqueio programado';
  const partes: string[] = [];
  if (marcacao.titulo) partes.push(ROTULO_TIPO_MARCACAO[marcacao.tipo]);
  if (marcacao.responsavelNome) partes.push(marcacao.responsavelNome);
  if (marcacao.pessoas)
    partes.push(`${marcacao.pessoas} ${marcacao.pessoas === 1 ? 'pessoa' : 'pessoas'}`);
  return partes.join(' · ');
}
